import { createSelector } from '@reduxjs/toolkit';
import uniqBy from 'lodash/uniqBy';

import { OFFER_STATUS, type TOfferStatus } from 'constants/offer';

import { isPreApprovalExchangeOffer } from 'shared/lib/isPreApprovalExchangeOffer';
import type { SliceStateFromReducer } from 'shared/types';
import type { TGroupType, TOffer } from 'shared/types/TRankingService';

import type { offersSlice } from './offers.slice';

export type TOffersStateFromReducer = SliceStateFromReducer<typeof offersSlice>;

export const offersSelector = (state: TOffersStateFromReducer) => state.offers.offers;
export const calcIdSelector = (state: TOffersStateFromReducer) => state.offers.calcId;
export const availableBanksSelector = (state: TOffersStateFromReducer) => state.offers.availableBanks;
export const isBentoRankingModelSelector = (state: TOffersStateFromReducer) => state.offers.isBentoRankingModel;

export const offersByGroupSelector = (group: TGroupType) =>
  createSelector(offersSelector, (offers) => offers.filter((offer) => offer.group === group));

export const firstOfferByGroupSelector = (group: TGroupType) =>
  createSelector(offersByGroupSelector(group), (offers) => offers[0] || null);

export const offerIdsByGroupSelector = (group: TGroupType) =>
  createSelector(offersByGroupSelector(group), (offers) => offers.map((offer) => offer?.id || ''));

const filterOffersByActiveStatus = (offers: TOffer[]) => {
  return offers.filter((offer) => offer.status !== OFFER_STATUS.NEW);
};

export const activeOffersSelector = createSelector(offersSelector, (offers) =>
  uniqBy(filterOffersByActiveStatus(offers), 'id'),
);

export const offerIdsByGroupWithActiveStatusSelector = (group: TGroupType) =>
  createSelector(offersByGroupSelector(group), (offers) =>
    filterOffersByActiveStatus(offers).map((offer) => offer?.id || ''),
  );

export const offersStatusCountByGroupSelector = (group: TGroupType) =>
  createSelector(offersByGroupSelector(group), (offers) => {
    const refused = offers.filter(
      (item) => item.status === OFFER_STATUS.REFUSED || item.status === OFFER_STATUS.FAILED,
    ).length;
    const approved = offers.filter((item) => item.status === OFFER_STATUS.ACCEPTED).length;
    const waiting = offers.filter((item) => item.status === OFFER_STATUS.QUEUED).length;

    return { refused, approved, waiting };
  });

export const areOffersLoadingSelector = (state: TOffersStateFromReducer) => Boolean(state.offers.isLoading);
export const isOffersErrorResponseSelector = (state: TOffersStateFromReducer) => Boolean(state.offers.isErrorResponse);
export const isOffersEmptySelector = (state: TOffersStateFromReducer) =>
  !state.offers.isLoading && state.offers.offers.length === 0;

export const selectIsAvailableMpl = (state: TOffersStateFromReducer) => state.offers.isAvailableMpl;

export const superRelevantGroupSelector = createSelector(
  [
    offersByGroupSelector('SuperRelevant'),
    offerIdsByGroupSelector('SuperRelevant'),
    offerIdsByGroupWithActiveStatusSelector('SuperRelevant'),
  ],
  (offers, offerIds, offerIdsWithActiveStatuses) => {
    return {
      offers,
      offerIds,
      offerIdsWithActiveStatuses,
      isAllOffersWithStatus: offerIds.length === offerIdsWithActiveStatuses.length,
    };
  },
);

export const hasPreapprovalsInProgressSelector = (groupType: TGroupType) =>
  createSelector(offersByGroupSelector(groupType), (offers) => {
    return offers.some((offer) => offer?.additionalInfo?.isPreapproving);
  });

export const leadGenHiddenExchangeOffersSelector = (state: TOffersStateFromReducer) =>
  state.offers.hiddenOffers?.filter(isPreApprovalExchangeOffer) || [];

export const hasQueuedOrAcceptedLeadGenOffersSelector = (state: TOffersStateFromReducer) =>
  state.offers.offers.some((offer) =>
    ([OFFER_STATUS.QUEUED, OFFER_STATUS.ACCEPTED] as TOfferStatus[]).includes(offer.status),
  );
